import { VERSION as VERSION$1 } from '@angular/cdk';
import * as i1 from '@angular/cdk/a11y';
import { _isTestEnvironment } from '@angular/cdk/platform';
import { DOCUMENT, TitleCasePipe, DatePipe } from '@angular/common';
import * as i0 from '@angular/core';
import { Version, InjectionToken, NgModule, Optional, Inject, LOCALE_ID, Injectable, booleanAttribute, Component, ViewEncapsulation, ChangeDetectionStrategy, Input, EventEmitter, Output, HostListener, Directive } from '@angular/core';
import { BehaviorSubject, fromEvent, Observable, Subject } from 'rxjs';
import { startWith, map } from 'rxjs/operators';
import { coerceBooleanProperty, coerceNumberProperty } from '@angular/cdk/coercion';
import { ENTER, SPACE, hasModifierKey } from '@angular/cdk/keycodes';

// tslint:disable-next-line: naming-convention
const Breakpoints = {
    Mobile: '(max-width: 642.99px)',
    Tablet: '(min-width: 643px) and (max-width: 1024.99px)',
    Desktop: '(min-width: 1025px) and (max-width: 1280.99px)',
    DesktopLarge: '(min-width: 1281px) and (max-width: 1440.99px)',
    Desktop2k: '(min-width: 1441px) and (max-width: 2560.99px)',
    Desktop4k: '(min-width: 2561px) and (max-width: 3840.99px)',
    Desktop5k: '(min-width: 3841px)',
    MobileDevice: '(max-width: 642.99px) and (orientation: portrait), ' +
        '(max-width: 1024.99px) and (orientation: landscape)',
    TabletDevice: '(min-width: 643px) and (max-width: 897.99px) and (orientation: portrait), ' +
        '(min-width: 1025px) and (max-width: 1280.99px) and (orientation: landscape)',
    DesktopDevice: '(min-width: 898px) and (orientation: portrait), ' +
        '(min-width: 1280px) and (orientation: landscape)',
    MobileDevicePortrait: '(max-width: 642.99px) and (orientation: portrait)',
    TabletDevicePortrait: '(min-width: 643px) and (max-width: 897.99px) and (orientation: portrait)',
    DesktopDevicePortrait: '(min-width: 898px) and (orientation: portrait)',
    MobileDeviceLandscape: '(max-width: 1024.99px) and (orientation: landscape)',
    TabletDeviceLandscape: '(min-width: 1025px) and (max-width: 1279.99px) and (orientation: landscape)',
    DesktopDeviceLandscape: '(min-width: 1280px) and (orientation: landscape)',
};
const SCALING_FACTOR_4K = 1.5;
const SCALING_FACTOR_5K = 2;

/** Current version of @sbb-esta/angular. */
const VERSION = new Version('17.8.2');

/* tslint:disable */
// **********************************************************************************************
// This code to access the global object is mostly copied from `@angular/angular/packages/core/src/util/global.ts`
const __globalThis = typeof globalThis !== 'undefined' && globalThis;
const __window = typeof window !== 'undefined' && window;
const __self = typeof self !== 'undefined' &&
    typeof WorkerGlobalScope !== 'undefined' &&
    self instanceof WorkerGlobalScope &&
    self;
const __global = typeof global !== 'undefined' && global;
// Always use __globalThis if available; this is the spec-defined global variable across all
// environments.
// Then fallback to __global first; in Node tests both __global and __window may be defined.
const _global = __globalThis || __global || __window || __self;

/* tslint:disable:callable-types */
/**
 * Tag a template literal string for localization.
 *
 * For example:
 *
 * ```ts
 * $localize `some string to localize`
 * ```
 *
 * **Providing meaning, description and id**
 *
 * You can optionally specify one or more of `meaning`, `description` and `id` for a localized
 * string by pre-pending it with a colon delimited block of the form:
 *
 * ```ts
 * $localize`:meaning|description@@id:source message text`;
 *
 * $localize`:meaning|:source message text`;
 * $localize`:description:source message text`;
 * $localize`:@@id:source message text`;
 * ```
 *
 * This format is the same as that used for `i18n` markers in Angular templates. See the
 * [Angular i18n guide](guide/i18n-common-prepare#mark-text-in-component-template).
 *
 * **Naming placeholders**
 *
 * If the template literal string contains expressions, then the expressions will be automatically
 * associated with placeholder names for you.
 *
 * For example:
 *
 * ```ts
 * $localize `Hi ${name}! There are ${items.length} items.`;
 * ```
 *
 * will generate a message-source of `Hi {$PH}! There are {$PH_1} items`.
 *
 * The recommended practice is to name the placeholder associated with each expression though.
 *
 * Do this by providing the placeholder name wrapped in `:` characters directly after the
 * expression. These placeholder names are stripped out of the rendered localized string.
 *
 * For example, to name the `items.length` expression placeholder `itemCount` you write:
 *
 * ```ts
 * $localize `There are ${items.length}:itemCount: items`;
 * ```
 *
 * **Escaping colon markers**
 *
 * If you need to use a `:` character directly at the start of a tagged string that has no
 * metadata block, or directly after a substitution expression that has no name you must escape
 * the `:` by preceding it with a backslash:
 *
 * For example:
 *
 * ```ts
 * // message has a metadata block so no need to escape colon
 * $localize `:some description::this message starts with a colon (:)`;
 * // no metadata block so the colon must be escaped
 * $localize `\:this message starts with a colon (:)`;
 * ```
 *
 * ```ts
 * // named substitution so no need to escape colon
 * $localize `${label}:label:: ${}`
 * // anonymous substitution so colon must be escaped
 * $localize `${label}\: ${}`
 * ```
 *
 * **Processing localized strings:**
 *
 * There are three scenarios:
 *
 * * **compile-time inlining**: the `$localize` tag is transformed at compile time by a
 * transpiler, removing the tag and replacing the template literal string with a translated
 * literal string from a collection of translations provided to the transpilation tool.
 *
 * * **run-time evaluation**: the `$localize` tag is a run-time function that replaces and
 * reorders the parts (static strings and expressions) of the template literal string with strings
 * from a collection of translations loaded at run-time.
 *
 * * **pass-through evaluation**: the `$localize` tag is a run-time function that simply evaluates
 * the original template literal string without applying any translations to the parts. This
 * version is used during development or where there is no need to translate the localized
 * template literals.
 *
 * @param messageParts a collection of the static parts of the template string.
 * @param expressions a collection of the values of each placeholder in the template string.
 * @returns the translated string, with the `messageParts` and `expressions` interleaved together.
 *
 * @globalApi
 * @publicApi
 */
// tslint:disable-next-line:naming-convention
const _$localize = function (messageParts, ...expressions) {
    if (_$localize.translate) {
        // Don't use array expansion here to avoid the compiler adding `__read()` helper unnecessarily.
        const translation = _$localize.translate(messageParts, expressions);
        messageParts = translation[0];
        expressions = translation[1];
    }
    let message = stripBlock(messageParts[0], messageParts.raw[0]);
    for (let i = 1; i < messageParts.length; i++) {
        message += expressions[i - 1] + stripBlock(messageParts[i], messageParts.raw[i]);
    }
    return message;
};
const BLOCK_MARKER = ':';
/**
 * Strip a delimited "block" from the start of the `messagePart`, if it is found.
 *
 * If a marker character (:) actually appears in the content at the start of a tagged string or
 * after a substitution expression, where a block has not been provided the character must be
 * escaped with a backslash, `\:`. This function checks for this by looking at the `raw`
 * messagePart, which should still contain the backslash.
 *
 * @param messagePart The cooked message part to process.
 * @param rawMessagePart The raw message part to check.
 * @returns the message part with the placeholder name stripped, if found.
 * @throws an error if the block is unterminated
 */
function stripBlock(messagePart, rawMessagePart) {
    return rawMessagePart.charAt(0) === BLOCK_MARKER
        ? messagePart.substring(findEndOfBlock(messagePart, rawMessagePart) + 1)
        : messagePart;
}
/**
 * Find the end of a "marked block" indicated by the first non-escaped colon.
 *
 * @param cooked The cooked string (where escaped chars have been processed)
 * @param raw The raw string (where escape sequences are still in place)
 *
 * @returns the index of the end of block marker
 * @throws an error if the block is unterminated
 */
function findEndOfBlock(cooked, raw) {
    /***********************************************************************************************
     * This function is repeated in `src/utils/messages.ts` and the two should be kept in sync.
     * The reason is that this file is marked as having side-effects, and if we import `messages.ts`
     * into it, the whole of `src/utils` will be included in this bundle and none of the functions
     * will be tree shaken.
     ***********************************************************************************************/
    for (let cookedIndex = 1, rawIndex = 1; cookedIndex < cooked.length; cookedIndex++, rawIndex++) {
        if (raw[rawIndex] === '\\') {
            rawIndex++;
        }
        else if (cooked[cookedIndex] === BLOCK_MARKER) {
            return cookedIndex;
        }
    }
    throw new Error(`Unterminated $localize metadata block in "${raw}".`);
}

const ɵvariant = new BehaviorSubject('standard');
function mixinVariant(base) {
    return class extends base {
        constructor(...args) {
            super(...args);
            this.variant = ɵvariant;
        }
        get variantSnapshot() {
            return ɵvariant.value;
        }
    };
}

/** @docs-private */
function SBB_SANITY_CHECKS_FACTORY() {
    return true;
}
/** Injection token that configures whether the SBB sanity checks are enabled. */
const SBB_SANITY_CHECKS = new InjectionToken('sbb-sanity-checks', {
    providedIn: 'root',
    factory: SBB_SANITY_CHECKS_FACTORY,
});
/**
 * Module that captures anything that should be loaded and/or run for *all* sbb-angular
 * components. This includes variant, etc.
 *
 * This module should be imported to each top-level component module (e.g. SbbTabsModule).
 */
class SbbCommonModule {
    constructor(highContrastModeDetector, _sanityChecks, _document) {
        this._sanityChecks = _sanityChecks;
        this._document = _document;
        /** Whether we've done the global sanity checks (e.g. a theme is loaded, there is a doctype). */
        this._hasDoneGlobalChecks = false;
        this._isDarkMode().subscribe((isDark) => {
            document.documentElement.classList.toggle('sbb-preferred-color-scheme-dark', isDark);
        });
        // Check variant configuration at the beginning, as this might cause components
        // to render differently.
        ɵvariant.next(this._document.documentElement.classList.contains('sbb-lean') ? 'lean' : 'standard');
        // While A11yModule also does this, we repeat it here to avoid importing A11yModule
        // in SbbCommonModule.
        highContrastModeDetector._applyBodyHighContrastModeCssClasses();
        patch$localize();
        if (!this._hasDoneGlobalChecks) {
            this._hasDoneGlobalChecks = true;
            if (typeof ngDevMode === 'undefined' || ngDevMode) {
                if (this._checkIsEnabled('doctype')) {
                    _checkDoctypeIsDefined(this._document);
                }
                if (this._checkIsEnabled('typography')) {
                    _checkTypographyIsPresent(this._document);
                }
                if (this._checkIsEnabled('version')) {
                    _checkCdkVersionMatch();
                }
            }
        }
    }
    /** Gets whether a specific sanity check is enabled. */
    _checkIsEnabled(name) {
        if (_isTestEnvironment()) {
            return false;
        }
        if (typeof this._sanityChecks === 'boolean') {
            return this._sanityChecks;
        }
        return !!this._sanityChecks[name];
    }
    _isDarkMode() {
        const query = window.matchMedia('(prefers-color-scheme: dark)');
        return fromEvent(query, 'change').pipe(startWith(query), map((event) => !!event.matches));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbCommonModule, deps: [{ token: i1.HighContrastModeDetector }, { token: SBB_SANITY_CHECKS, optional: true }, { token: DOCUMENT }], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.7", ngImport: i0, type: SbbCommonModule }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbCommonModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbCommonModule, decorators: [{
            type: NgModule
        }], ctorParameters: () => [{ type: i1.HighContrastModeDetector }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [SBB_SANITY_CHECKS]
                }] }, { type: Document, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }] });
/** Checks that the page has a doctype. */
// tslint:disable-next-line: naming-convention
function _checkDoctypeIsDefined(doc) {
    if (!doc.doctype) {
        console.warn('Current document does not have a doctype. This may cause ' +
            'some sbb-angular components not to behave as expected.');
    }
}
/** Checks that the typography has been included. */
// tslint:disable-next-line: naming-convention
function _checkTypographyIsPresent(doc) {
    // We need to assert that the `body` is defined, because these checks run very early
    // and the `body` won't be defined if the consumer put their scripts in the `head`.
    if (!doc.body || typeof getComputedStyle !== 'function') {
        return;
    }
    // The --sbb-scaling-factor variable is available, if our typography is loaded as
    // a majority of variables and rules depend on this variable.
    const missingScalingFactorVariable = !getComputedStyle(doc.documentElement).getPropertyValue('--sbb-scaling-factor');
    if (missingScalingFactorVariable) {
        console.warn('Could not find @sbb-esta/angular typography. Most @sbb-esta/angular ' +
            'components may not work as expected.');
    }
}
/** Checks whether the @sbb-esta/angular version matches the CDK major version. */
// tslint:disable-next-line: naming-convention
function _checkCdkVersionMatch() {
    if (VERSION.major !== VERSION$1.major) {
        console.warn(`The @sbb-esta/angular version (${VERSION.full}) does not match the Angular CDK major version` +
            ` (${VERSION$1.full}).\nPlease ensure the major versions of these two packages match.`);
    }
}
/**
 * If an app doesn't use localize, on production environment there is no $localize function.
 * In order to provide our translations we patch the $localize object with the original function.
 */
function patch$localize() {
    // In production mode and if localize is off, $localize is not a function
    if (typeof _global.$localize !== 'function') {
        // Merge $localize function with provided $localize object
        _global.$localize = Object.assign(_$localize, _global.$localize);
    }
}

function mixinDisabled(base) {
    return class extends base {
        get disabled() {
            return this._disabled;
        }
        set disabled(value) {
            this._disabled = coerceBooleanProperty(value);
        }
        constructor(...args) {
            super(...args);
            this._disabled = false;
        }
    };
}

/**
 * Class that tracks the error state of a component.
 * @docs-private
 */
// tslint:disable-next-line:class-name
class _ErrorStateTracker {
    constructor(_defaultMatcher, ngControl, _parentFormGroup, _parentForm, _stateChanges) {
        this._defaultMatcher = _defaultMatcher;
        this.ngControl = ngControl;
        this._parentFormGroup = _parentFormGroup;
        this._parentForm = _parentForm;
        this._stateChanges = _stateChanges;
        /** Whether the tracker is currently in an error state. */
        this.errorState = false;
    }
    /** Updates the error state based on the provided error state matcher. */
    updateErrorState() {
        const oldState = this.errorState;
        const parent = this._parentFormGroup || this._parentForm;
        const matcher = this.matcher || this._defaultMatcher;
        const control = this.ngControl ? this.ngControl.control : null;
        // Note: the null check here shouldn't be necessary, but there's an internal
        // test that appears to pass an object whose `isErrorState` isn't a function.
        const newState = typeof matcher?.isErrorState === 'function' ? matcher.isErrorState(control, parent) : false;
        if (newState !== oldState) {
            this.errorState = newState;
            this._stateChanges.next();
        }
    }
}
function mixinErrorState(base) {
    return class extends base {
        /** Whether the component is in an error state. */
        get errorState() {
            return this._getTracker().errorState;
        }
        set errorState(value) {
            this._getTracker().errorState = value;
        }
        /** An object used to control the error state of the component. */
        get errorStateMatcher() {
            return this._getTracker().matcher;
        }
        set errorStateMatcher(value) {
            this._getTracker().matcher = value;
        }
        /** Updates the error state based on the provided error state matcher. */
        updateErrorState() {
            this._getTracker().updateErrorState();
        }
        _getTracker() {
            if (!this._tracker) {
                this._tracker = new _ErrorStateTracker(this._defaultErrorStateMatcher, this.ngControl, this._parentFormGroup, this._parentForm, this.stateChanges);
            }
            return this._tracker;
        }
        constructor(...args) {
            super(...args);
        }
    };
}

/**
 * Mixin to augment a directive with an initialized property that will emits when ngOnInit ends.
 * @deprecated To be removed.
 * @breaking-change 19.0.0
 */
function mixinInitialized(base) {
    return class extends base {
        constructor(...args) {
            super(...args);
            /** Whether this directive has been marked as initialized. */
            this._isInitialized = false;
            /**
             * List of subscribers that subscribed before the directive was initialized. Should be notified
             * during _markInitialized. Set to null after pending subscribers are notified, and should
             * not expect to be populated after.
             */
            this._pendingSubscribers = [];
            /**
             * Observable stream that emits when the directive initializes. If already initialized, the
             * subscriber is stored to be notified once _markInitialized is called.
             */
            this.initialized = new Observable((subscriber) => {
                // If initialized, immediately notify the subscriber. Otherwise store the subscriber to notify
                // when _markInitialized is called.
                if (this._isInitialized) {
                    this._notifySubscriber(subscriber);
                }
                else {
                    // tslint:disable-next-line: no-non-null-assertion
                    this._pendingSubscribers.push(subscriber);
                }
            });
        }
        /**
         * Marks the state as initialized and notifies pending subscribers. Should be called at the end
         * of ngOnInit.
         * @docs-private
         */
        _markInitialized() {
            if (this._isInitialized && (typeof ngDevMode === 'undefined' || ngDevMode)) {
                throw Error('This directive has already been marked as initialized and ' +
                    'should not be called twice.');
            }
            this._isInitialized = true;
            // tslint:disable-next-line: no-non-null-assertion
            this._pendingSubscribers.forEach(this._notifySubscriber);
            this._pendingSubscribers = null;
        }
        /** Emits and completes the subscriber stream (should only emit once). */
        _notifySubscriber(subscriber) {
            subscriber.next();
            subscriber.complete();
        }
    };
}

function mixinTabIndex(base, defaultTabIndex = 0) {
    return class extends base {
        get tabIndex() {
            return this.disabled ? -1 : this._tabIndex;
        }
        set tabIndex(value) {
            // If the specified tabIndex value is null or undefined, fall back to the default value.
            this._tabIndex = value != null ? coerceNumberProperty(value) : this.defaultTabIndex;
        }
        constructor(...args) {
            super(...args);
            this._tabIndex = defaultTabIndex;
            this.defaultTabIndex = defaultTabIndex;
        }
    };
}

/** Adapts type `D` to be usable as a date by cdk-based components that work with dates. */
class SbbDateAdapter {
    /**
     * Attempts to deserialize a value to a valid date object. This is different from parsing in that
     * deserialize should only accept non-ambiguous, locale-independent formats (e.g. a ISO 8601
     * string). The default implementation does not allow any deserialization, it simply checks that
     * the given value is already a valid date object or null. The `<sbb-datepicker>` will call this
     * method on all of it's `@Input()` properties that accept dates. It is therefore possible to
     * support passing values from your backend directly to these properties by overriding this method
     * to also deserialize the format used by your backend.
     * @param value The value to be deserialized into a date object.
     * @returns The deserialized date object, either a valid date, null if the value can be
     *     deserialized into a null date (e.g. the empty string), or an invalid date.
     */
    deserialize(value) {
        if (value == null || (this.isDateInstance(value) && this.isValid(value))) {
            return value;
        }
        return this.invalid();
    }
    /**
     * Sets the locale used for all dates.
     * @param locale The new locale.
     */
    setLocale(locale) {
        this._locale = locale;
    }
    /**
     * Compares two dates.
     * @param first The first date to compare.
     * @param second The second date to compare.
     * @returns 0 if the dates are equal, a number less than 0 if the first date is earlier,
     *     a number greater than 0 if the first date is later.
     */
    compareDate(first, second) {
        return (this.getYear(first) - this.getYear(second) ||
            this.getMonth(first) - this.getMonth(second) ||
            this.getDate(first) - this.getDate(second));
    }
    /**
     * Checks if two dates are equal.
     * @param first The first date to check.
     * @param second The second date to check.
     * @returns Whether the two dates are equal.
     *     Null dates are considered equal to other null dates.
     */
    sameDate(first, second) {
        if (first && second) {
            const firstValid = this.isValid(first);
            const secondValid = this.isValid(second);
            if (firstValid && secondValid) {
                return !this.compareDate(first, second);
            }
            return firstValid === secondValid;
        }
        return first === second;
    }
    /**
     * Clamp the given date between min and max dates.
     * @param date The date to clamp.
     * @param min The minimum value to allow. If null or omitted no min is enforced.
     * @param max The maximum value to allow. If null or omitted no max is enforced.
     * @returns `min` if `date` is less than `min`, `max` if date is greater than `max`,
     *     otherwise `date`.
     */
    clampDate(date, min, max) {
        if (min && this.compareDate(date, min) < 0) {
            return min;
        }
        if (max && this.compareDate(date, max) > 0) {
            return max;
        }
        return date;
    }
}

const SBB_DATE_FORMATS = new InjectionToken('sbb-date-formats');

const SBB_DATE_PIPE_DATE_FORMATS = {
    dateInput: 'EEEEEE, dd.MM.yyyy',
    dateInputPure: 'dd.MM.yyyy',
    dateA11yLabel: 'dd MM yyyy',
};

const SBB_DATEPICKER_2DIGIT_YEAR_PIVOT = new InjectionToken('SBB_DATEPICKER_2DIGIT_YEAR_PIVOT');
const SBB_DATEPICKER_PREVENT_OVERFLOW = new InjectionToken('SBB_DATEPICKER_PREVENT_OVERFLOW');

/**
 * Matches strings that have the form of a valid RFC 3339 string
 * (https://tools.ietf.org/html/rfc3339). Note that the string may not actually be a valid date
 * because the regex will match strings an with out of bounds month, date, etc.
 */
const ISO_8601_REGEX = /^\d{4}-\d{2}-\d{2}(?:T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|(?:(?:\+|-)\d{2}:\d{2}))?)?$/;
/** Creates an array and fills it with values. */
function range(length, valueFunction) {
    const valuesArray = Array(length);
    for (let i = 0; i < length; i++) {
        valuesArray[i] = valueFunction(i);
    }
    return valuesArray;
}
class SbbNativeDateAdapter extends SbbDateAdapter {
    constructor(_locale, yearPivot, preventOverflow) {
        super();
        this._locale = _locale;
        this._titleCasePipe = new TitleCasePipe();
        this._datePipe = new DatePipe(_locale);
        this._yearPivot = typeof yearPivot === 'number' ? yearPivot : new Date().getFullYear() - 1975;
        this._preventOverflow = typeof preventOverflow === 'boolean' ? preventOverflow : false;
    }
    getYear(date) {
        return date.getFullYear();
    }
    getMonth(date) {
        return date.getMonth();
    }
    getDate(date) {
        return date.getDate();
    }
    getDayOfWeek(date) {
        return date.getDay();
    }
    getMonthName(date) {
        return this._datePipe.transform(date, 'LLLL');
    }
    getMonthNames(style) {
        let format;
        switch (style) {
            case 'long':
                format = 'LLLL';
                break;
            case 'narrow':
                format = 'LLLLL';
                break;
            default:
                format = 'LLL';
                break;
        }
        return range(12, (i) => this._datePipe.transform(new Date(2017, i, 1), format));
    }
    getDateNames() {
        return range(31, (i) => this._datePipe.transform(new Date(2017, 0, i + 1), 'd'));
    }
    getDayOfWeekNames(style) {
        let format;
        switch (style) {
            case 'long':
                format = 'EEEE';
                break;
            case 'narrow':
                format = 'EEEEE';
                break;
            default:
                format = 'EEE';
                break;
        }
        return range(7, (i) => this._datePipe.transform(new Date(2017, 0, i + 1), format));
    }
    getYearName(date) {
        return this._datePipe.transform(date, 'yyyy');
    }
    getFirstDayOfWeek() {
        return 1;
    }
    getNumDaysInMonth(date) {
        const lastDayOfMonth = new Date(0);
        lastDayOfMonth.setFullYear(date.getFullYear(), date.getMonth() + 1, 0);
        lastDayOfMonth.setHours(0, 0, 0, 0);
        return lastDayOfMonth.getDate();
    }
    clone(date) {
        return new Date(date.valueOf());
    }
    createDate(year, month, date) {
        // Check for invalid month and date (except upper bound on date which we have to check after
        // creating the Date).
        if (month < 0 || month > 11) {
            throw Error(`Invalid month index "${month}". Month index has to be between 0 and 11.`);
        }
        if (date < 1) {
            throw Error(`Invalid date "${date}". Date has to be greater than 0.`);
        }
        const result = this._createDateWithOverflow(year, month, date);
        // Check that the date wasn't above the upper bound for the month, causing the month to overflow
        if (result.getMonth() !== month) {
            throw Error(`Invalid date "${date}" for month with index "${month}".`);
        }
        return result;
    }
    today() {
        const date = new Date();
        date.setHours(0, 0, 0, 0);
        return date;
    }
    parse(value) {
        if (typeof value === 'number') {
            return new Date(value);
        }
        else if (typeof value === 'string') {
            if (this._preventOverflow && this._isOverflowingDate(value)) {
                return new Date(NaN);
            }
            return this._parseStringDate(value);
        }
        return null;
    }
    format(date, displayFormat) {
        if (!this.isValid(date)) {
            throw Error('NativeDateAdapter: Cannot format invalid date.');
        }
        return this._titleCasePipe.transform(this._datePipe.transform(date, displayFormat));
    }
    addCalendarYears(date, years) {
        return this.addCalendarMonths(date, years * 12);
    }
    addCalendarMonths(date, months) {
        const targetMonth = date.getMonth() + months;
        const dateWithCorrectMonth = new Date(0);
        dateWithCorrectMonth.setFullYear(date.getFullYear(), targetMonth, 1);
        dateWithCorrectMonth.setHours(0, 0, 0, 0);
        const daysInMonth = this.getNumDaysInMonth(dateWithCorrectMonth);
        const newDate = this.clone(date);
        // Adapt last day of month for shorter months
        newDate.setMonth(targetMonth, Math.min(daysInMonth, date.getDate()));
        return newDate;
    }
    addCalendarDays(date, days) {
        const newDate = this.clone(date);
        newDate.setDate(newDate.getDate() + days);
        return newDate;
    }
    toIso8601(date) {
        return this._datePipe.transform(date, 'yyyy-MM-dd');
    }
    /**
     * Returns the given value if given a valid Date or null. Deserializes valid ISO 8601 strings
     * (https://www.ietf.org/rfc/rfc3339.txt) into valid Dates and empty string into null. Returns an
     * invalid date for all other values.
     */
    deserialize(value) {
        if (typeof value === 'string') {
            if (!value) {
                return null;
            }
            // The `Date` constructor accepts formats other than ISO 8601, so we need to make sure the
            // string is the right format first.
            if (ISO_8601_REGEX.test(value)) {
                const date = new Date(value);
                if (this.isValid(date)) {
                    return date;
                }
            }
        }
        return super.deserialize(value);
    }
    isDateInstance(obj) {
        return obj instanceof Date;
    }
    isValid(date) {
        return !isNaN(date.valueOf());
    }
    invalid() {
        return new Date(NaN);
    }
    _splitStringDate(value) {
        const match = /^(\w+,[ ]?)?(\d+)\.(\d+)\.(\d+)$/.exec(value);
        if (!match) {
            return null;
        }
        return [+match[4], +match[3] - 1, +match[2]];
    }
    _parseStringDate(value) {
        const parts = this._splitStringDate(value);
        if (!parts) {
            return null;
        }
        const date = this._createDateWithOverflow(...parts);
        return this._normalizeYear(date);
    }
    _normalizeYear(date) {
        if (date.getFullYear() < 0 || date.getFullYear() >= 100) {
            return date;
        }
        const yearOffset = date.getFullYear() <= this._yearPivot ? 2000 : 1900;
        return new Date(date.getFullYear() + yearOffset, date.getMonth(), date.getDate());
    }
    /** Creates a date but allows the month and date to overflow. */
    _createDateWithOverflow(year, month, date) {
        const result = new Date(year, month, date);
        // We need to correct for the fact that JS native Date treats years in range [0, 99] as
        // abbreviations for 19xx.
        if (year >= 0 && year < 100) {
            result.setFullYear(this.getYear(result) - 1900);
        }
        return result;
    }
    /** Checks whether a given date string contains an overflowing date. */
    _isOverflowingDate(value) {
        const parts = this._splitStringDate(value);
        const date = this._parseStringDate(value);
        if (!parts || !date) {
            return false;
        }
        const [year, month, day] = parts;
        return date.getFullYear() !== year || date.getMonth() !== month || date.getDate() !== day;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbNativeDateAdapter, deps: [{ token: LOCALE_ID }, { token: SBB_DATEPICKER_2DIGIT_YEAR_PIVOT, optional: true }, { token: SBB_DATEPICKER_PREVENT_OVERFLOW, optional: true }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbNativeDateAdapter }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbNativeDateAdapter, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [LOCALE_ID]
                }] }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [SBB_DATEPICKER_2DIGIT_YEAR_PIVOT]
                }] }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [SBB_DATEPICKER_PREVENT_OVERFLOW]
                }] }] });

class SbbLeanDateAdapter extends SbbNativeDateAdapter {
    constructor(_locale, yearPivot, preventOverflow) {
        super(_locale, yearPivot, preventOverflow);
        this._locale = _locale;
    }
    _parseStringDate(value) {
        return super._parseStringDate(value) || this._parseStringDateLean(value);
    }
    _splitStringDate(value) {
        return super._splitStringDate(value) || this._splitStringDateLean(value);
    }
    _parseStringDateLean(value) {
        const split = this._splitStringDateLean(value);
        if (!split) {
            return null;
        }
        return this._normalizeYear(this._createDateWithOverflow(...split));
    }
    _splitStringDateLean(value) {
        const match = /^(\w+,[ ]?)?(\d{2})(\d{2})(\d{2}|\d{4})$/.exec(value);
        if (!match) {
            return null;
        }
        return [+match[4], +match[3] - 1, +match[2]];
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbLeanDateAdapter, deps: [{ token: LOCALE_ID }, { token: SBB_DATEPICKER_2DIGIT_YEAR_PIVOT, optional: true }, { token: SBB_DATEPICKER_PREVENT_OVERFLOW, optional: true }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbLeanDateAdapter }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbLeanDateAdapter, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [LOCALE_ID]
                }] }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [SBB_DATEPICKER_2DIGIT_YEAR_PIVOT]
                }] }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [SBB_DATEPICKER_PREVENT_OVERFLOW]
                }] }] });

const SBB_LEAN_DATE_ADAPTER = {
    provide: SbbDateAdapter,
    useClass: SbbLeanDateAdapter,
};
function provideLeanDateAdapter() {
    return [
        SBB_LEAN_DATE_ADAPTER,
        { provide: SBB_DATE_FORMATS, useValue: SBB_DATE_PIPE_DATE_FORMATS },
    ];
}

function provideNativeDateAdapter() {
    return [
        { provide: SbbDateAdapter, useClass: SbbNativeDateAdapter },
        { provide: SBB_DATE_FORMATS, useValue: SBB_DATE_PIPE_DATE_FORMATS },
    ];
}

/** Provider that defines how form controls behave with regards to displaying error messages. */
class SbbErrorStateMatcher {
    isErrorState(control, form) {
        return !!(control && control.invalid && (control.touched || (form && form.submitted)));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbErrorStateMatcher, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbErrorStateMatcher, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbErrorStateMatcher, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });
/** Error state matcher that matches when a control is invalid and dirty. */
class SbbShowOnDirtyErrorStateMatcher {
    isErrorState(control, form) {
        return !!(control && control.invalid && (control.dirty || (form && form.submitted)));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbShowOnDirtyErrorStateMatcher, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbShowOnDirtyErrorStateMatcher }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbShowOnDirtyErrorStateMatcher, decorators: [{
            type: Injectable
        }] });

/**
 * Injection token used to provide the parent component to options.
 */
const SBB_OPTION_PARENT_COMPONENT = new InjectionToken('SBB_OPTION_PARENT_COMPONENT');

// Notes on the accessibility pattern used for `sbb-optgroup`.
// The option group has two different "modes": regular and inert. The regular mode uses the
// recommended a11y pattern which has `role="group"` on the group element with `aria-labelledby`
// pointing to the label. This works for `sbb-select`, but it seems to hit a bug for autocomplete
// under VoiceOver where the group doesn't get read out at all. The bug appears to be that if
// there's __any__ a11y-related attribute on the group (e.g. `role` or `aria-labelledby`),
// VoiceOver on Safari won't read it out.
// We've introduced the `inert` mode as a workaround. Under this mode, all a11y attributes are
// removed from the group, and we get the screen reader to read out the group label by mirroring it
// inside an invisible element in the option. This is sub-optimal, because the screen reader will
// repeat the group label on each navigation, whereas the default pattern only reads the group when
// the user enters a new group. The following alternate approaches were considered:
// 1. Reading out the group label using the `LiveAnnouncer` solves the problem, but we can't control
//    when the text will be read out so sometimes it comes in too late or never if the user
//    navigates quickly.
// 2. `<sbb-option aria-describedby="groupLabel"` - This works on Safari, but VoiceOver in Chrome
//    won't read out the description at all.
// 3. `<sbb-option aria-labelledby="optionLabel groupLabel"` - This works on Chrome, but Safari
//     doesn't read out the text at all. Furthermore, on
// Counter for unique group ids.
let uniqueOptgroupIdCounter = 0;
/**
 * Injection token that can be used to reference instances of `SbbOptgroup`. It serves as
 * alternative token to the actual `SbbOptgroup` class which could cause unnecessary
 * retention of the class and its component metadata.
 */
const SBB_OPTGROUP = new InjectionToken('SbbOptgroup');
class SbbOptgroup {
    constructor(parent) {
        /** Whether the group is disabled. */
        this.disabled = false;
        /** Unique id for the underlying label. */
        this._labelId = `sbb-optgroup-label-${uniqueOptgroupIdCounter++}`;
        this._inert = parent?.inertGroups ?? false;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbOptgroup, deps: [{ token: SBB_OPTION_PARENT_COMPONENT, optional: true }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "16.1.0", version: "17.3.7", type: SbbOptgroup, selector: "sbb-optgroup", inputs: { disabled: ["disabled", "disabled", booleanAttribute], label: "label" }, host: { properties: { "attr.role": "_inert ? null : \"group\"", "attr.aria-disabled": "_inert ? null : disabled.toString()", "attr.aria-labelledby": "_inert ? null : _labelId", "class.sbb-disabled": "disabled" }, classAttribute: "sbb-optgroup sbb-menu-group" }, providers: [{ provide: SBB_OPTGROUP, useExisting: SbbOptgroup }], exportAs: ["sbbOptgroup"], ngImport: i0, template: "<label class=\"sbb-label sbb-optgroup-label\" [id]=\"_labelId\"\n  >{{ label }} <ng-content></ng-content\n></label>\n<ng-content select=\"sbb-option, ng-container, sbb-option-hint, sbb-autocomplete-hint\"></ng-content>\n", changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbOptgroup, decorators: [{
            type: Component,
            args: [{ selector: 'sbb-optgroup', exportAs: 'sbbOptgroup', encapsulation: ViewEncapsulation.None, changeDetection: ChangeDetectionStrategy.OnPush, inputs: ['disabled'], host: {
                        class: 'sbb-optgroup sbb-menu-group',
                        '[attr.role]': '_inert ? null : "group"',
                        '[attr.aria-disabled]': '_inert ? null : disabled.toString()',
                        '[attr.aria-labelledby]': '_inert ? null : _labelId',
                        '[class.sbb-disabled]': 'disabled',
                    }, providers: [{ provide: SBB_OPTGROUP, useExisting: SbbOptgroup }], template: "<label class=\"sbb-label sbb-optgroup-label\" [id]=\"_labelId\"\n  >{{ label }} <ng-content></ng-content\n></label>\n<ng-content select=\"sbb-option, ng-container, sbb-option-hint, sbb-autocomplete-hint\"></ng-content>\n" }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [SBB_OPTION_PARENT_COMPONENT]
                }, {
                    type: Optional
                }] }], propDecorators: { label: [{
                type: Input
            }], disabled: [{
                type: Input,
                args: [{ transform: booleanAttribute }]
            }] } });

class SbbPseudoCheckbox {
    constructor() {
        /** Display state of the checkbox. */
        this.state = 'unchecked';
        /** Whether the checkbox is disabled. */
        this.disabled = false;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbPseudoCheckbox, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.7", type: SbbPseudoCheckbox, selector: "sbb-pseudo-checkbox", inputs: { state: "state", disabled: "disabled" }, host: { properties: { "class.sbb-selection-disabled": "this.disabled", "class.sbb-selection-checked": "this.state === \"checked\"", "class.sbb-selection-indeterminate": "this.state === \"indeterminate\"" }, classAttribute: "sbb-pseudo-checkbox" }, ngImport: i0, template: "<div class=\"sbb-selection-container\">\n  <span class=\"sbb-selection-container-checked\">\n    <svg\n      xmlns=\"http://www.w3.org/2000/svg\"\n      width=\"24\"\n      height=\"24\"\n      viewBox=\"0 0 24 24\"\n      focusable=\"false\"\n    >\n      <polyline\n        fill=\"none\"\n        fill-rule=\"evenodd\"\n        stroke=\"#000\"\n        stroke-width=\"1\"\n        points=\"6 12 10 16 19 7.01\"\n      />\n    </svg>\n  </span>\n</div>\n", changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbPseudoCheckbox, decorators: [{
            type: Component,
            args: [{ selector: 'sbb-pseudo-checkbox', host: {
                        class: 'sbb-pseudo-checkbox',
                        '[class.sbb-selection-disabled]': 'this.disabled',
                        '[class.sbb-selection-checked]': 'this.state === "checked"',
                        '[class.sbb-selection-indeterminate]': 'this.state === "indeterminate"',
                    }, encapsulation: ViewEncapsulation.None, changeDetection: ChangeDetectionStrategy.OnPush, template: "<div class=\"sbb-selection-container\">\n  <span class=\"sbb-selection-container-checked\">\n    <svg\n      xmlns=\"http://www.w3.org/2000/svg\"\n      width=\"24\"\n      height=\"24\"\n      viewBox=\"0 0 24 24\"\n      focusable=\"false\"\n    >\n      <polyline\n        fill=\"none\"\n        fill-rule=\"evenodd\"\n        stroke=\"#000\"\n        stroke-width=\"1\"\n        points=\"6 12 10 16 19 7.01\"\n      />\n    </svg>\n  </span>\n</div>\n" }]
        }], propDecorators: { state: [{
                type: Input
            }], disabled: [{
                type: Input
            }] } });

/**
 * Option IDs need to be unique across components, so this counter exists outside of
 * the component definition.
 */
let uniqueIdCounter = 0;
/** Event object emitted by SbbOption when selected or deselected. */
class SbbOptionSelectionChange {
    constructor(
    /** Reference to the option that emitted the event. */
    source, 
    /** Whether the change in the option's value was a result of a user action. */
    isUserInput = false) {
        this.source = source;
        this.isUserInput = isUserInput;
    }
}
class SbbOption {
    /** Whether the wrapping component is in multiple selection mode. */
    get multiple() {
        return this._parent && this._parent.multiple;
    }
    /** Whether or not the option is currently selected. */
    get selected() {
        return this._selected;
    }
    /** Whether the option is disabled. */
    get disabled() {
        return (this.group && this.group.disabled) || this._disabled;
    }
    set disabled(value) {
        this._disabled = coerceBooleanProperty(value);
        this._changeDetectorRef.markForCheck();
    }
    constructor(_element, _document, _changeDetectorRef, _parent, group) {
        this._element = _element;
        this._document = _document;
        this._changeDetectorRef = _changeDetectorRef;
        this._parent = _parent;
        this.group = group;
        this._selected = false;
        this._active = false;
        this._disabled = false;
        this._mostRecentViewValue = '';
        this._highlighted = false;
        /** The unique ID of the option. */
        this.id = `sbb-option-${uniqueIdCounter++}`;
        /** Event emitted when the option is selected or deselected. */
        // tslint:disable-next-line:no-output-on-prefix
        this.onSelectionChange = new EventEmitter();
        /** Emits when the state of the option changes and any parents have to be notified. */
        this._stateChanges = new Subject();
    }
    /**
     * Whether or not the option is currently active and ready to be selected.
     * An active option displays styles as if it is focused, but the
     * focus is actually retained somewhere else. This comes in handy
     * for components like autocomplete where focus must remain on the input.
     */
    get active() {
        return this._active;
    }
    /**
     * The displayed value of the option. It is necessary to show the selected option in the
     * select's trigger.
     */
    get viewValue() {
        // TODO(kara): Add input property alternative for node envs.
        return (this._getHostElement().textContent || '').trim();
    }
    /** Selects the option. */
    select() {
        if (!this.selected) {
            this._selected = true;
            this._changeDetectorRef.markForCheck();
            this._emitSelectionChangeEvent();
        }
    }
    /** Deselects the option. */
    deselect(emitEvent = true) {
        if (this._selected) {
            this._selected = false;
            this._changeDetectorRef.markForCheck();
            if (emitEvent) {
                this._emitSelectionChangeEvent();
            }
        }
    }
    /** Sets focus onto this option. */
    focus(_origin, options) {
        // Note that we aren't using `_origin`, but we need to keep it because some internal consumers
        // use `SbbOption` in a `FocusKeyManager` and we need it to match `FocusableOption`.
        const element = this._getHostElement();
        if (typeof element.focus === 'function') {
            element.focus(options);
        }
    }
    /**
     * This method sets display styles on the option to make it appear
     * active. This is used by the ActiveDescendantKeyManager so key
     * events will display the proper options as active on arrow key events.
     */
    setActiveStyles() {
        if (!this.active) {
            this._active = true;
            this._changeDetectorRef.markForCheck();
        }
    }
    /**
     * This method removes display styles on the option that made it appear
     * active. This is used by the ActiveDescendantKeyManager so key
     * events will display the proper options as active on arrow key events.
     */
    setInactiveStyles() {
        if (this.active) {
            this._active = false;
            this._changeDetectorRef.markForCheck();
        }
    }
    /** Gets the label to be used when determining whether the option should be focused. */
    getLabel() {
        return this.viewValue;
    }
    /** Ensures the option is selected when activated from the keyboard. */
    _handleKeydown(event) {
        if ((event.keyCode === ENTER || event.keyCode === SPACE) && !hasModifierKey(event)) {
            this._selectViaInteraction();
            // Prevent the page from scrolling down and form submits.
            event.preventDefault();
        }
    }
    /**
     * `Selects the option while indicating the selection came from the user. Used to
     * determine if the select's view -> model callback should be invoked.`
     */
    _selectViaInteraction() {
        if (!this.disabled) {
            this._selected = this.multiple ? !this.selected : true;
            this._changeDetectorRef.markForCheck();
            this._emitSelectionChangeEvent(true);
        }
    }
    /** Returns the correct tabindex for the option depending on disabled state. */
    _getTabIndex() {
        return this.disabled ? '-1' : '0';
    }
    /** Gets the host DOM element. */
    _getHostElement() {
        return this._element.nativeElement;
    }
    ngAfterViewChecked() {
        // Since parent components could be using the option's label to display the selected values
        // (e.g. `sbb-select`) and they don't have a way of knowing if the option's label has changed
        // we have to check for changes in the DOM ourselves and dispatch an event. These checks are
        // relatively cheap, however we still limit them only to selected options in order to avoid
        // hitting the DOM too often.
        if (this._selected) {
            const viewValue = this.viewValue;
            if (viewValue !== this._mostRecentViewValue) {
                if (this._mostRecentViewValue) {
                    this._stateChanges.next();
                }
                this._mostRecentViewValue = viewValue;
            }
        }
    }
    ngOnDestroy() {
        this._stateChanges.complete();
    }
    /**
     * Highlights a text part of the option by wrapping it with a strong element.
     * @docs-private
     */
    _highlight(value, localeNormalizer) {
        if (this._originalInnerHtml === undefined) {
            this._originalInnerHtml = this._element.nativeElement.innerHTML;
        }
        else if (value === this._highlightValue) {
            return;
        }
        else if (this._highlighted) {
            this._element.nativeElement.innerHTML = this._originalInnerHtml;
            this._highlighted = false;
        }
        this._highlightValue = value;
        if (value && this._originalInnerHtml) {
            const normalizer = localeNormalizer || ((v) => v);
            value = normalizer(value);
            // Escape all regex characters
            const escapedValue = value.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
            const matcher = new RegExp(`${escapedValue}+`, 'i');
            const nodes = this._findAllTextNodesWithMatch(matcher, normalizer);
            nodes.forEach((n) => this._highlightNode(n, matcher, normalizer));
            this._highlighted = !!nodes.length;
        }
    }
    _findAllTextNodesWithMatch(matcher, localeNormalizer, node = this._element.nativeElement) {
        const nodes = [];
        const childNodes = node.childNodes;
        for (let i = 0; i < childNodes.length; i++) {
            const childNode = childNodes[i];
            // Text nodes are nodeType 3
            if (childNode.nodeType === 3 && matcher.test(localeNormalizer(childNode.textContent || ''))) {
                nodes.push(childNode);
            }
            else if (childNode.childNodes.length) {
                nodes.push(...this._findAllTextNodesWithMatch(matcher, localeNormalizer, childNode));
            }
        }
        return nodes;
    }
    /**
     * Replace the content with a (partial) match, with text nodes and span elements
     * which contain the highlightable content.
     * @param node The node with (partial) content to highlight.
     * @param matcher The content to highlight.
     * @param localeNormalizer function to normalize locale chars
     */
    _highlightNode(node, matcher, localeNormalizer) {
        const nodes = [];
        const doc = this._document;
        matcher.lastIndex = 0;
        let text = node.textContent || '';
        let match;
        do {
            match = localeNormalizer(text).match(matcher);
            if (!match) {
                nodes.push(doc.createTextNode(text));
                continue;
            }
            else if (match.index) {
                nodes.push(doc.createTextNode(text.substring(0, match.index)));
                text = text.substring(match.index);
            }
            const strong = doc.createElement('strong');
            strong.textContent = text.substring(0, match[0].length);
            nodes.push(strong);
            text = text.substring(match[0].length);
        } while (match);
        const parent = node.parentNode;
        nodes.forEach((n) => parent.insertBefore(n, node));
        parent.removeChild(node);
    }
    /** Emits the selection change event. */
    _emitSelectionChangeEvent(isUserInput = false) {
        this.onSelectionChange.emit(new SbbOptionSelectionChange(this, isUserInput));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbOption, deps: [{ token: i0.ElementRef }, { token: DOCUMENT }, { token: i0.ChangeDetectorRef }, { token: SBB_OPTION_PARENT_COMPONENT, optional: true }, { token: SBB_OPTGROUP, optional: true }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "17.3.7", type: SbbOption, selector: "sbb-option", inputs: { value: "value", id: "id", disabled: "disabled" }, outputs: { onSelectionChange: "onSelectionChange" }, host: { attributes: { "role": "option" }, listeners: { "keydown": "_handleKeydown($event)", "click": "_selectViaInteraction()" }, properties: { "attr.tabindex": "_getTabIndex()", "class.sbb-selected": "selected", "class.sbb-option-multiple": "multiple", "class.sbb-focused": "active", "id": "id", "attr.aria-selected": "selected", "attr.aria-disabled": "disabled.toString()", "class.sbb-disabled": "disabled" }, classAttribute: "sbb-option sbb-menu-item sbb-link-reset" }, exportAs: ["sbbOption"], ngImport: i0, template: "@if (multiple) {\n  <sbb-pseudo-checkbox\n    [disabled]=\"disabled\"\n    [state]=\"selected ? 'checked' : 'unchecked'\"\n  ></sbb-pseudo-checkbox>\n}\n<ng-content></ng-content>\n\n<!-- See a11y notes inside optgroup.ts for context behind this element. -->\n@if (group && group._inert) {\n  <span class=\"cdk-visually-hidden\">({{ group.label }})</span>\n}\n", styles: [".sbb-option.sbb-menu-item{cursor:default}.sbb-pseudo-checkbox{display:inline-block;margin-right:calc(0.5333333333rem*var(--sbb-scaling-factor));margin-bottom:calc(-0.3rem*var(--sbb-scaling-factor));overflow:hidden;line-height:0}.sbb-pseudo-checkbox .sbb-selection-container{margin:0}"], dependencies: [{ kind: "component", type: SbbPseudoCheckbox, selector: "sbb-pseudo-checkbox", inputs: ["state", "disabled"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbOption, decorators: [{
            type: Component,
            args: [{ selector: 'sbb-option', exportAs: 'sbbOption', encapsulation: ViewEncapsulation.None, changeDetection: ChangeDetectionStrategy.OnPush, host: {
                        class: 'sbb-option sbb-menu-item sbb-link-reset',
                        role: 'option',
                        '[attr.tabindex]': '_getTabIndex()',
                        '[class.sbb-selected]': 'selected',
                        '[class.sbb-option-multiple]': 'multiple',
                        '[class.sbb-focused]': 'active',
                        '[id]': 'id',
                        '[attr.aria-selected]': 'selected',
                        '[attr.aria-disabled]': 'disabled.toString()',
                        '[class.sbb-disabled]': 'disabled',
                    }, template: "@if (multiple) {\n  <sbb-pseudo-checkbox\n    [disabled]=\"disabled\"\n    [state]=\"selected ? 'checked' : 'unchecked'\"\n  ></sbb-pseudo-checkbox>\n}\n<ng-content></ng-content>\n\n<!-- See a11y notes inside optgroup.ts for context behind this element. -->\n@if (group && group._inert) {\n  <span class=\"cdk-visually-hidden\">({{ group.label }})</span>\n}\n", styles: [".sbb-option.sbb-menu-item{cursor:default}.sbb-pseudo-checkbox{display:inline-block;margin-right:calc(0.5333333333rem*var(--sbb-scaling-factor));margin-bottom:calc(-0.3rem*var(--sbb-scaling-factor));overflow:hidden;line-height:0}.sbb-pseudo-checkbox .sbb-selection-container{margin:0}"] }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }, { type: i0.ChangeDetectorRef }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [SBB_OPTION_PARENT_COMPONENT]
                }] }, { type: SbbOptgroup, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [SBB_OPTGROUP]
                }] }], propDecorators: { value: [{
                type: Input
            }], id: [{
                type: Input
            }], disabled: [{
                type: Input
            }], onSelectionChange: [{
                type: Output
            }], _handleKeydown: [{
                type: HostListener,
                args: ['keydown', ['$event']]
            }], _selectViaInteraction: [{
                type: HostListener,
                args: ['click']
            }] } });
/**
 * Counts the amount of option group labels that precede the specified option.
 * @param optionIndex Index of the option at which to start counting.
 * @param options Flat list of all of the options.
 * @param optionGroups Flat list of all of the option groups.
 * @docs-private
 */
function countGroupLabelsBeforeOption(optionIndex, options, optionGroups) {
    if (optionGroups.length) {
        const optionsArray = options.toArray();
        const groups = optionGroups.toArray();
        let groupCounter = 0;
        for (let i = 0; i < optionIndex + 1; i++) {
            if (optionsArray[i].group && optionsArray[i].group === groups[groupCounter]) {
                groupCounter++;
            }
        }
        return groupCounter;
    }
    return 0;
}
/**
 * Determines the position to which to scroll a panel in order for an option to be into view.
 * @param optionOffset Offset of the option from the top of the panel.
 * @param optionHeight Height of the options.
 * @param currentScrollPosition Current scroll position of the panel.
 * @param panelHeight Height of the panel.
 * @docs-private
 */
function getOptionScrollPosition(optionOffset, optionHeight, currentScrollPosition, panelHeight) {
    if (optionOffset < currentScrollPosition) {
        return optionOffset;
    }
    if (optionOffset + optionHeight > currentScrollPosition + panelHeight) {
        return Math.max(0, optionOffset - panelHeight + optionHeight);
    }
    return currentScrollPosition;
}

/**
 * Option-hint IDs need to be unique across components, so this counter exists outside of
 * the component definition.
 */
let nextId = 0;
class SbbOptionHint {
    constructor() {
        /** Unique ID to be used by autocomplete trigger's "aria-controls" property. */
        this.id = `sbb-option-hint-${nextId++}`;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbOptionHint, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.3.7", type: SbbOptionHint, selector: "sbb-option-hint", host: { properties: { "attr.id": "id" }, classAttribute: "sbb-option-hint sbb-label" }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbOptionHint, decorators: [{
            type: Directive,
            args: [{
                    selector: 'sbb-option-hint',
                    host: {
                        class: 'sbb-option-hint sbb-label',
                        '[attr.id]': 'id',
                    },
                }]
        }] });

class SbbOptionModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbOptionModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.7", ngImport: i0, type: SbbOptionModule, declarations: [SbbOption, SbbOptionHint, SbbOptgroup, SbbPseudoCheckbox], imports: [SbbCommonModule], exports: [SbbOption, SbbOptionHint, SbbOptgroup, SbbPseudoCheckbox] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbOptionModule, imports: [SbbCommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbOptionModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [SbbCommonModule],
                    declarations: [SbbOption, SbbOptionHint, SbbOptgroup, SbbPseudoCheckbox],
                    exports: [SbbOption, SbbOptionHint, SbbOptgroup, SbbPseudoCheckbox],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { Breakpoints, SBB_DATEPICKER_2DIGIT_YEAR_PIVOT, SBB_DATEPICKER_PREVENT_OVERFLOW, SBB_DATE_FORMATS, SBB_DATE_PIPE_DATE_FORMATS, SBB_LEAN_DATE_ADAPTER, SBB_OPTGROUP, SBB_OPTION_PARENT_COMPONENT, SBB_SANITY_CHECKS, SBB_SANITY_CHECKS_FACTORY, SCALING_FACTOR_4K, SCALING_FACTOR_5K, SbbCommonModule, SbbDateAdapter, SbbErrorStateMatcher, SbbLeanDateAdapter, SbbNativeDateAdapter, SbbOptgroup, SbbOption, SbbOptionHint, SbbOptionModule, SbbOptionSelectionChange, SbbPseudoCheckbox, SbbShowOnDirtyErrorStateMatcher, VERSION, _ErrorStateTracker, countGroupLabelsBeforeOption, getOptionScrollPosition, mixinDisabled, mixinErrorState, mixinInitialized, mixinTabIndex, mixinVariant, provideLeanDateAdapter, provideNativeDateAdapter, ɵvariant };
